import type { AppAction } from '../actions';
import type { ContainerViewModel } from '../types';
import { keyArrayToRecord } from '../utils';

export const INITIAL_Z_INDEX = 1;

export interface ContainerState {
    byId: Record<string, ContainerViewModel>;
    activeContainerId?: string;
    maximizedContainerId?: string | null;
    zIndex: Record<string, number>;
    mruOrder: string[];
}

export const initialState: ContainerState = {
    byId: {},
    activeContainerId: undefined,
    maximizedContainerId: undefined,
    zIndex: {},
    mruOrder: [],
};

const sortContainersByZIndex = (state: ContainerState, containerId?: string): ContainerState => {
    if (!containerId) {
        return state;
    }

    const sortedIds = Object.entries(state.zIndex)
        .sort((a, b) => a[1] - b[1])
        .map(([id]) => id)
        .filter((id) => id !== containerId);

    sortedIds.push(containerId);

    state.zIndex = Object.fromEntries(sortedIds.map((id, idx) => [id, INITIAL_Z_INDEX + idx]));

    return state;
};

export const container = (state = initialState, action: AppAction): ContainerState => {
    switch (action.type) {
        case 'clearWorkspaceState': {
            return { ...initialState };
        }

        case 'initializeWorkspace': {
            const byId = {
                ...state.byId,
                ...keyArrayToRecord(action.containers, (container) => container.id),
            };
            const containerIds = Object.keys(byId);
            const zIndex = Object.fromEntries(containerIds.map((id, idx) => [id, INITIAL_Z_INDEX + idx]));
            return {
                ...state,
                maximizedContainerId: action.workspace.maximizedContainerId,
                byId,
                zIndex,
                mruOrder: containerIds as string[],
            };
        }

        case 'addContainer': {
            return {
                ...state,
                activeContainerId: action.container.id,
                byId: {
                    ...state.byId,
                    [action.container.id]: action.container,
                },
                zIndex: {
                    ...state.zIndex,
                    [action.container.id]:
                        Object.values(state.zIndex).reduce(
                            (prevZIndex, zIndex) => Math.max(prevZIndex, zIndex),
                            INITIAL_Z_INDEX,
                        ) + 1,
                },
                mruOrder: [action.container.id, ...state.mruOrder],
            };
        }

        case 'removeContainer': {
            const updatedContainersByKey: Record<string, ContainerViewModel> = { ...state.byId };
            delete updatedContainersByKey[action.containerId];
            const wasMaximizedContainer = action.containerId === state.maximizedContainerId;
            const wasActiveContainer = action.containerId === state.activeContainerId;

            return {
                ...state,
                byId: updatedContainersByKey,
                ...(wasMaximizedContainer ? { maximizedContainerId: undefined } : {}),
                ...(wasActiveContainer ? { activeContainerId: undefined } : {}),
                mruOrder: state.mruOrder.filter((id) => id !== action.containerId),
            };
        }

        case 'addTab': {
            return {
                ...state,
                byId: {
                    ...state.byId,
                    [action.containerId]: {
                        ...(state.byId[action.containerId] ?? {}),
                        activeTabId: action.tab.id,
                        tabIds: [...(state.byId[action.containerId]?.tabIds ?? []), action.tab.id],
                    },
                },
            };
        }

        case 'removeTab': {
            const id = action.containerId;

            return {
                ...state,
                byId: {
                    ...state.byId,
                    [id]: {
                        ...(state.byId[id] ?? {}),
                        tabIds: (state.byId[id]?.tabIds ?? []).filter((x) => x !== action.tabId),
                        activeTabId: action.newActiveTabId,
                    },
                },
            };
        }

        case 'updateActiveTab': {
            const id = action.containerId;

            if (!state.byId[id]?.tabIds.includes(action.tabId)) {
                // if the container doesn't have this tab, don't set it to active
                return state;
            }

            return {
                ...state,
                byId: {
                    ...state.byId,
                    [id]: {
                        ...(state.byId[id] ?? {}),
                        activeTabId: action.tabId,
                    },
                },
            };
        }

        case 'setActiveContainer': {
            const containerId = action.containerId;

            return sortContainersByZIndex(
                {
                    ...state,
                    activeContainerId: containerId,
                    mruOrder: containerId
                        ? [containerId, ...state.mruOrder.filter((id) => id !== containerId)]
                        : state.mruOrder,
                },
                containerId,
            );
        }

        case 'cycleActiveContainer': {
            const containerId = action.containerId;

            return sortContainersByZIndex(
                {
                    ...state,
                    activeContainerId: containerId,
                },
                containerId,
            );
        }

        case 'moveTab': {
            const tabKeysCopy = [...(state.byId[action.containerId]?.tabIds ?? [])];
            const draggedTabKey = tabKeysCopy[action.startIndex]; // get the tab key to move
            tabKeysCopy.splice(action.startIndex, 1); // remove the tab key from its current position
            tabKeysCopy.splice(action.endIndex, 0, draggedTabKey); // insert the tab key at its new position

            return {
                ...state,
                byId: {
                    ...state.byId,
                    [action.containerId]: {
                        ...(state.byId[action.containerId] ?? {}),
                        tabIds: tabKeysCopy,
                    },
                },
            };
        }

        case 'setMaximizedContainer': {
            const containerId = action.containerId;

            return sortContainersByZIndex(
                {
                    ...state,
                    maximizedContainerId: containerId,
                },
                containerId,
            );
        }

        default:
            return state;
    }
};
